// Terugverdientijd-berekening voor alle consumentenproposities (batterij, EMS, warmtepomp).
// Rekenmodel uit de adviestools (DP_thuisbatterij_tool / DP_warmtepomp_tool), zie
// "Ecoportaal – fix terugverdientijd-berekening" (15 okt 2026).
// Batterij + EMS: btw-teruggave -> excl. btw. Warmtepomp: incl. btw minus ISDE.

export const SAVINGS_PARAMS = {
    prijsStroom: 0.30, prijsTerugleverkosten: 0.15, prijsTerugleververgoeding: 0.07,
    prijsGas: 1.45,
    stijgingGas: 0.05, stijgingStroom: 0.02, // per jaar, cumulatief
    // batterij
    epexMarge: 0.22, onbalansPerKwh: 250,
    cycliPerDag: 1.4, cycliPerDagMetWp: 2.0,
    rendement: 0.85, cannibalisatie: 0.45, // EPEX en onbalans delen de capaciteit
    onbalansNetFactor: 0.85, // 0.85 via eigen contract, 0.75 via andere aanbieder
    dagenOverschot: 200, realisatie: 0.70, pvDirectFrac: 0.30,
    emsLosAandeel: 0.5, // EMS los op batterij ander merk
    // warmtepomp
    scopAllElectric: 3.3, scopHybride: 4.2,
    hybrideDekking: 0.80, // deel verwarmingsgas dat hybride overneemt
    gasKoken: 50, gasTapwaterPerPersoon: 50,
    vastrechtGas: 240, // vervalt bij all-electric (gasaansluiting eraf)
    btw: 0.21, horizon: 30,
};
const P = SAVINGS_PARAMS;

export const BATTERY = 'BATTERY';
export const EMS = 'EMS';
export const HYBRIDE = 'HYBRIDE';
export const ALL_ELECTRIC = 'ALL_ELECTRIC';

// Categorienamen verschillen per propositie ("Batterij", "Batterij 3 fase", "All-electric warmtepomp").
// Onderdeelcategorieën zoals "Batterijsysteem" of "Warmtepompsysteem" zijn geen product.
export const typeFromCategoryName = (name) => {
    const n = (name || '').trim().toLowerCase();
    if (/^(thuis)?batterij(en)?( \d[- ]?fase)?$/.test(n)) return BATTERY;
    if (n === 'ems' || n.startsWith('ems ')) return EMS;
    if (n.includes('warmtepomp') && n.includes('hybride')) return HYBRIDE;
    if (n.includes('warmtepomp') && /all[- ]?electric/.test(n)) return ALL_ELECTRIC;
    return null;
};

// Omschrijving "nominaal-bruikbaar", bijv. "10.36-11.52"
const nominalKwh = p => {
    const parts = String(p.description || '').split('-');
    const kwh = parseFloat(parts[0]);
    return parts.length > 1 && kwh > 0 ? kwh : 0;
};

// Losse-batterijproposities (BATTERY*) bevatten alleen batterijen, ongeacht de categorie
// (daar staan batterijen ook onder "Laadpaal", "Noodstroom voorziening" of zonder categorie).
const isBatteryProposition = p => String(p.subType || '').toUpperCase().startsWith('BATTERY');

// externalReference-codes uit de multiproduct-offertetemplate (NTN_offerte_multiproducten_ROI.twig)
export const EXTREF_LAADPAAL = '14';
export const EXTREF_ZONNEPANELEN = '15';
export const EXTREF_ISOLATIE = '21';

// Productsoort voor de ROI-invoer: eerst de externalReference, anders de categorienaam.
// Batterijproposities slaan we over: daar staan batterijen soms onder "Laadpaal".
const isKind = (p, extRef, namePattern, categoryNames) => {
    if (String(p.externalReference ?? '') === extRef) return true;
    if (isBatteryProposition(p)) return false;
    return namePattern.test(categoryNames[p.subCategoryID] ?? p.subCategoryName ?? '');
};

export const productCalcType = (p, categoryNames = {}) => {
    if (p.calcType) return p.calcType;
    if (isBatteryProposition(p)) return nominalKwh(p) > 0 ? BATTERY : null;
    const name = categoryNames[p.subCategoryID] ?? p.subCategoryName;
    if (name) return typeFromCategoryName(name);
    return nominalKwh(p) > 0 ? BATTERY : null;
};

const kwOf = p => parseFloat(((p.name || p.description || '').match(/(\d+)\s*kw(?!h)/i) || [])[1]) || 0;

// ISDE zoals de offerte die aftrekt (subsidieregel onder de warmtepomp), anders een schatting.
const isdePerUnit = p => {
    const subsidy = (p.children || []).filter(c => c.isSubsidy && +c.totalAmount < 0);
    if (subsidy.length) return subsidy.reduce((s, c) => s - +c.totalAmount, 0);
    return kwOf(p) >= 14 ? 3700 : 3025;
};

function batterijOpbrengst(kwh, {teruglevering, metWp}) {
    const pvProductie = teruglevering / (1 - P.pvDirectFrac);
    const overschot = pvProductie * (1 - P.pvDirectFrac);
    const opgeslagen = Math.min(kwh * 0.85, overschot / P.dagenOverschot) * P.dagenOverschot * P.realisatie;
    const zon = opgeslagen * Math.max(0, P.prijsStroom + P.prijsTerugleverkosten - P.prijsTerugleververgoeding);
    const cycli = metWp ? P.cycliPerDagMetWp : P.cycliPerDag;
    const epex = kwh * cycli * P.epexMarge * 365 * P.rendement * P.cannibalisatie;
    const onbalans = kwh * P.onbalansPerKwh * P.cannibalisatie * P.onbalansNetFactor;
    return {zon, handel: epex + onbalans};
}

function warmtepomp(type, {gas, personen, teruglevering, heeftEms}) {
    const gasVerwarm = Math.max(0, gas - P.gasKoken - personen * P.gasTapwaterPerPersoon);
    const kwhVerwarm = gasVerwarm * 9.77;
    let gasRest, kwhExtra;
    if (type === ALL_ELECTRIC) {
        gasRest = 0;
        kwhExtra = (kwhVerwarm + personen * P.gasTapwaterPerPersoon * 9.77) / P.scopAllElectric;
    } else {
        gasRest = gas - gasVerwarm * P.hybrideDekking;
        kwhExtra = kwhVerwarm * P.hybrideDekking / P.scopHybride;
    }
    const pvProductie = teruglevering / (1 - P.pvDirectFrac);
    let pvDekking = 0;
    if (pvProductie > 0 && kwhExtra > 0) {
        const r = pvProductie / kwhExtra;
        pvDekking = r >= 2 ? 0.40 : r >= 1.5 ? 0.35 : r >= 1 ? 0.30 : r >= 0.7 ? 0.25 : r >= 0.4 ? 0.18 : r * 0.4;
        if (!heeftEms) pvDekking *= 0.5;
    }
    const restPrijs = heeftEms ? Math.min(0.15, P.prijsStroom * 0.55) : P.prijsStroom;
    let effPrijs = (1 - pvDekking) * restPrijs;
    if (heeftEms && pvProductie > 0) effPrijs = Math.min(effPrijs, 0.10);
    return {
        gasBesparing: (gas - Math.max(0, gasRest)) * P.prijsGas + (type === ALL_ELECTRIC ? P.vastrechtGas : 0),
        stroomKosten: kwhExtra * effPrijs,
    };
}

// Terugverdientijd met cumulatieve prijsstijging (gas +5%/jr, stroom +2%/jr)
function terugverdientijd(netto, j1) {
    let cum = 0;
    for (let j = 1; j <= P.horizon; j++) {
        const b = j1.gas * (1 + P.stijgingGas) ** (j - 1)
            + (j1.stroomOpbrengst - j1.stroomKosten) * (1 + P.stijgingStroom) ** (j - 1);
        const prev = cum;
        cum += b;
        if (b > 0 && cum >= netto) return j - 1 + (netto - prev) / b;
    }
    return null;
}

/**
 * @param {Array} cart producten met quantity > 0 (ShopProductResource-vorm)
 * @param {Object} extraData order.extraData (usage, estimation, discount, existingBatteryKwh)
 * @param {Object} categoryNames {subCategoryID: naam}, aanvulling op subCategoryName
 */
export function calculateSavings(cart, extraData, categoryNames = {}) {
    const x = extraData || {};
    const of = t => cart.filter(p => productCalcType(p, categoryNames) === t);
    const sum = (l, f) => l.reduce((s, p) => s + f(p) * (+p.quantity || 0), 0);

    const batteries = of(BATTERY), ems = of(EMS);
    const hybride = of(HYBRIDE), allElectric = of(ALL_ELECTRIC);
    const wpProducts = [...hybride, ...allElectric];
    const wpType = allElectric.length ? ALL_ELECTRIC : hybride.length ? HYBRIDE : null;

    const kwh = sum(batteries, nominalKwh);
    const teruglevering = +x.usage?.returnElectricity || 0;
    const gas = +x.usage?.gas || 0;
    const personen = +x.estimation?.persons || 2;
    const bestaandeKwh = +x.existingBatteryKwh || 0; // alleen bij EMS zonder batterij
    const heeftEms = kwh > 0 || ems.length > 0;

    let stroomOpbrengst = 0;
    if (kwh > 0) {
        const r = batterijOpbrengst(kwh, {teruglevering, metWp: !!wpType});
        stroomOpbrengst = r.zon + r.handel;
    } else if (ems.length && bestaandeKwh > 0) {
        stroomOpbrengst = batterijOpbrengst(bestaandeKwh, {teruglevering: 0, metWp: !!wpType}).handel * P.emsLosAandeel;
    }

    // Zonder gasverbruik is er geen warmtepompbesparing; de warmtepomp telt dan ook niet mee in de prijs.
    let gasB = 0, stroomK = 0, wpBruto = 0, isde = 0;
    if (wpType && gas > 0) {
        ({gasBesparing: gasB, stroomKosten: stroomK} = warmtepomp(wpType, {gas, personen, teruglevering, heeftEms}));
        wpBruto = sum(wpProducts, p => +p.subAmount) * (1 + P.btw);
        isde = sum(wpProducts, isdePerUnit);
    }
    const stroomBruto = (kwh > 0 || stroomOpbrengst > 0) ? sum([...batteries, ...ems], p => +p.subAmount) : 0;

    const korting = x.discount || {};
    const netto = Math.max(0, (stroomBruto + wpBruto) * (1 - (+korting.percentage || 0) / 100)
        - (stroomBruto + wpBruto > 0 ? (+korting.loyaltyDiscount || 0) : 0) - isde);
    const jaar1 = stroomOpbrengst + gasB - stroomK;

    return {
        batteryCapacity: kwh,
        batterySavings: Math.round(stroomOpbrengst),
        heatPumpSavings: Math.round(gasB - stroomK),
        netInvestment: Math.round(netto),
        totalSavings: jaar1 > 0 ? Math.round(jaar1) : 0,
        paybackPeriod: jaar1 > 0 ? terugverdientijd(netto, {gas: gasB, stroomOpbrengst, stroomKosten: stroomK}) : null,
        hasHeatPump: !!wpType,
        hasEmsWithoutBattery: ems.length > 0 && kwh === 0,
    };
}

// Zet de uitkomst op order.extraData. batteryPrice blijft het mandjetotaal excl. btw:
// 2Solar gebruikt dat als offertebedrag voor templates zonder data-2solar-amount.
export function applySavings(order, cart, categoryNames = {}) {
    const x = order.extraData;
    const r = calculateSavings(cart, x, categoryNames);
    x.batteryCapacity = r.batteryCapacity;
    x.batteryPrice = cart.reduce((s, p) => s + (+p.subAmount || 0) * (+p.quantity || 0), 0);
    x.savings = {
        ...(x.savings || {}),
        totalSavings: r.totalSavings,
        paybackPeriod: r.paybackPeriod,
        batterySavings: r.batterySavings,
        heatPumpSavings: r.heatPumpSavings,
        netInvestment: r.netInvestment,
    };
    return r;
}

// Rekent dit product subsidie uit (subsidieregels met een subsidiecode)? Alleen dan tonen we
// woninggegevens, terugverdieninvoer, isolatie-advies en subsidie; andere proposities blijven zoals ze waren.
export const usesSubsidyCalculation = products =>
    (products || []).some(p => (p.children || []).some(c => c.subsidyCode));

// Heeft deze catalogus isolatiepakketten? Dan vraagt de flow vooraf naar isolatie-advies.
export const catalogHasIsolation = (products, categoryNames = {}) =>
    usesSubsidyCalculation(products) && (products || []).some(p => isKind(p, EXTREF_ISOLATIE, /isolatie/i, categoryNames));

// Welke extra invoer de berekening nodig heeft voor dit mandje (gasverbruik, bestaande batterij).
export function savingsInputs(products, categoryNames = {}) {
    const inCart = products.filter(p => p.quantity > 0);
    const types = inCart.map(p => productCalcType(p, categoryNames));
    const needsGas = types.includes(HYBRIDE) || types.includes(ALL_ELECTRIC);
    const hasIsolation = inCart.some(p => isKind(p, EXTREF_ISOLATIE, /isolatie/i, categoryNames));
    const hasSolar = inCart.some(p => isKind(p, EXTREF_ZONNEPANELEN, /zonnepane/i, categoryNames));
    const hasChargingStation = inCart.some(p => isKind(p, EXTREF_LAADPAAL, /laadpa/i, categoryNames));
    const subsidyCalc = usesSubsidyCalculation(products);
    return {
        needsGas,
        needsExistingBattery: types.includes(EMS) && !types.includes(BATTERY),
        // Woninggegevens bepalen de ISDE (warmtepomp, isolatie) en gemeentelijke subsidie
        needsBuilding: subsidyCalc && (needsGas || hasIsolation),
        // ROI-invoer per product voor de multiproduct-offerte (extraData.roiInput)
        needsIsolation: subsidyCalc && hasIsolation,
        needsHeatPump: subsidyCalc && needsGas,
        needsSolar: subsidyCalc && hasSolar,
        needsChargingStation: subsidyCalc && hasChargingStation,
        needsRoiInput: subsidyCalc && (hasIsolation || needsGas || hasSolar || hasChargingStation),
    };
}
